import React, { useEffect, useRef, useState } from 'react';
import { Clock, Camera, Loader2, Check, Repeat } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { compressPhoto, UPLOAD_TIMEOUT_MS } from '../lib/photo';
import { photoErrorInfo } from '../lib/uploadErrors';
import { HHChip } from '../pages/admin/AdminHappyHour';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const discTxt = (g, d) => (d != null && Number(d) !== Number(g)) ? `Q${Number(g ?? 0).toFixed(2)} gas / Q${Number(d).toFixed(2)} diésel` : `Q${Number(g ?? 0).toFixed(2)}`;


const LABELS = { vpower: 'V-Power', super: 'Súper', regular: 'Regular', diesel: 'Diesel', ulsd: 'ULSD' };
const ORDER = ['vpower', 'super', 'regular', 'diesel', 'ulsd'];
const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const HappyHourCards = () => {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const targetRef = useRef(null);
  const { user } = useAuth();
  const isTM = user?.role === 'territory_manager';

  const load = () => api.get('/happy-hours').then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const [showHistory, setShowHistory] = useState(false);
  const ACTIVE = ['pending', 'approved', 'approved_series', 'active', 'awaiting_close'];
  // De cada serie solo se muestra la SIGUIENTE fecha programada (las demás esperan su turno)
  const nextBySeries = {};
  rows.forEach(r => {
    if (r.series_id && r.status === 'approved' && (!nextBySeries[r.series_id] || r.start_at < nextBySeries[r.series_id])) {
      nextBySeries[r.series_id] = r.start_at;
    }
  });
  const activeRows = rows.filter(r => {
    if (!ACTIVE.includes(r.status)) return false;
    // Tarjetas de serie aprobada van dentro de "Ver series" (las pendientes siguen visibles)
    if (r.is_series && r.status !== 'pending') return false;
    if (r.series_id && r.status === 'approved') return r.start_at === nextBySeries[r.series_id];
    return true;
  });
  const seriesParents = rows.filter(r => r.is_series && ACTIVE.includes(r.status) && r.status !== 'pending');
  const historyRows = rows.filter(r => !ACTIVE.includes(r.status));
  const collapsedCount = seriesParents.length + historyRows.length;
  const visible = showHistory ? [...activeRows, ...seriesParents, ...historyRows.slice(0, 20)] : activeRows;
  if (activeRows.length === 0 && collapsedCount === 0) return null;

  const pickPhoto = (hh, kind) => { targetRef.current = { hh, kind }; fileRef.current?.click(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    const t = targetRef.current;
    if (!f || !t) return;
    const { hh, kind } = t;
    setUploading(hh.id);
    try {
      const cf = await compressPhoto(f);
      const fd = new FormData();
      fd.append('photo', cf, 'monolito.jpg');
      const ep = kind === 'start' ? `/happy-hours/${hh.id}/start-photo` : `/happy-hours/${hh.id}/close`;
      await api.post(ep, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS });
      toast.success(kind === 'start' ? 'Foto del monolito con precios HH enviada' : 'Happy Hour cerrado · foto del monolito enviada');
      load();
    } catch (err) {
      const det = err.response?.data?.detail;
      if (typeof det === 'string') toast.error(det, { duration: 8000 });
      else { const info = photoErrorInfo(err); toast.error(info.title, { description: info.hint, duration: 8000 }); }
    } finally { setUploading(null); }
  };

  const cancelSeries = async (hh) => {
    if (!window.confirm(`¿Cancelar las fechas restantes de la serie de ${hh.station_name}?`)) return;
    try {
      const r = await api.post(`/happy-hours/series/${hh.id}/cancel`);
      toast.success(`Serie cancelada · ${r.data.cancelled_occurrences} fecha(s) restante(s) canceladas`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cancelar la serie'); }
  };

  // Tiempo cumplido aunque el scheduler aún no marque awaiting_close
  const isTimeUp = (hh) => hh.status === 'awaiting_close' || (hh.status === 'active' && hh.end_at && new Date(hh.end_at) <= new Date());
  // Foto de inicio disponible desde la aprobación (hasta 24 h antes del inicio)
  const startReady = (hh) => hh.status === 'active' || (hh.status === 'approved' && hh.start_at && (new Date(hh.start_at) - Date.now()) <= 24 * 3600 * 1000);

  return (
    <div className="mb-5" data-testid="hh-cards-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-wider text-[#8a8579] flex items-center gap-1.5"><Clock size={12}/> Happy Hour</p>
        {collapsedCount > 0 && (
          <button
            onClick={() => setShowHistory(v => !v)}
            className="text-[10.5px] px-2.5 py-1 rounded-full border border-[#e8e5de] bg-white text-[#5a5a5a] active:bg-[#f3f0e9]"
            data-testid="hh-history-toggle"
          >
            {showHistory ? 'Ocultar' : `Ver series e históricos (${collapsedCount})`}
          </button>
        )}
      </div>
      {visible.length === 0 && (
        <p className="text-[11.5px] text-[#8a8579] bg-white border border-[#e8e5de] rounded-xl px-3.5 py-3" data-testid="hh-no-active">
          Sin Happy Hours activos.
        </p>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="hh-photo-input"/>
      <div className="space-y-2">
        {visible.map(hh => hh.is_series ? (
          <div key={hh.id} className="bg-white border border-[#c7d2fe] rounded-xl p-3.5" data-testid={`hh-series-card-${hh.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Repeat size={12} className="text-[#3730a3]"/>
              <p className="text-[13px] font-medium">{hh.station_name}</p>
              <HHChip s={hh.status}/>
            </div>
            <p className="text-[11.5px] text-[#5a5a5a] mt-1">
              Serie: los <strong>{hh.recurrence?.label}</strong> · {hh.recurrence?.start_time_gt} – {hh.recurrence?.end_time_gt} (GT)
              {hh.discount_approved != null ? <> · baja {discTxt(hh.discount_approved, hh.discount_diesel_approved)}</> : <> · baja solicitada {discTxt(hh.discount_requested, hh.discount_diesel_requested)}</>}
              {hh.occurrences_created != null && <> · {hh.occurrences_created} fecha(s)</>}
            </p>
            {hh.status === 'pending' && <p className="text-[11px] text-[#92400e] mt-0.5">Esperando aprobación de Pricing.</p>}
            {isTM && ['pending', 'approved_series'].includes(hh.status) && (
              <Button variant="outline" onClick={() => cancelSeries(hh)} className="mt-2 h-8 rounded-full text-[11.5px] border-[#991b1b]/40 text-[#991b1b]" data-testid={`hh-series-cancel-${hh.id}`}>
                Cancelar fechas restantes
              </Button>
            )}
          </div>
        ) : (
          <div key={hh.id} className={`bg-white border rounded-xl p-3.5 ${hh.status === 'awaiting_close' ? 'border-[#fca5a5]' : hh.status === 'active' ? 'border-[#86efac]' : 'border-[#e8e5de]'}`} data-testid={`hh-card-${hh.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[13px] font-medium">{hh.station_name}</p>
              <HHChip s={hh.status}/>
              {hh.series_id && (
                <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-[#e0e7ff] text-[#3730a3] uppercase tracking-wider font-medium">
                  <Repeat size={9} className="inline mr-0.5 -mt-px"/> {hh.series_label || 'Serie'}
                </span>
              )}
            </div>
            <p className="text-[11.5px] text-[#5a5a5a] mt-1">
              {fmt(hh.start_at)} → {fmt(hh.end_at)}
              {hh.discount_approved != null ? <> · baja {discTxt(hh.discount_approved, hh.discount_diesel_approved)}</> : <> · baja solicitada {discTxt(hh.discount_requested, hh.discount_diesel_requested)}</>}
            </p>
            {(hh.review_notes || '').trim() && ['approved', 'active', 'awaiting_close'].includes(hh.status) && (
              <p className="text-[11.5px] text-[#92400e] bg-[#fef3c7] border border-[#fde68a] rounded px-2 py-1.5 mt-1.5 font-medium" data-testid={`hh-pricing-note-${hh.id}`}>
                ⚠ Pricing: {hh.review_notes}
              </p>
            )}
            {hh.hh_prices && ['approved', 'active'].includes(hh.status) && (
              <p className="text-[11px] text-[#166534] mt-0.5 font-mono tabular-nums">
                HH: {ORDER.filter(k => hh.hh_prices[k]?.self != null).map(k => `${LABELS[k]} Q${hh.hh_prices[k].self.toFixed(2)}`).join(' · ')}
              </p>
            )}
            {(startReady(hh) || ['awaiting_close', 'closed'].includes(hh.status)) && (
              hh.start_photo_at
                ? <p className="text-[11px] text-[#166534] mt-1 flex items-center gap-1"><Check size={11}/> Foto del cambio subida {fmt(hh.start_photo_at)}</p>
                : <p className="text-[11px] text-[#991b1b] mt-1">Falta la foto del monolito con los precios HH</p>
            )}
            {isTimeUp(hh) && (
              <>
                <p className="text-[11.5px] text-[#991b1b] mt-1 font-medium">El tiempo se cumplió — regresa los precios y sube la foto del monolito de regreso.</p>
                {(hh.post_prices || hh.vigente_prices) && (
                  <p className="text-[11px] text-[#1e40af] mt-0.5 font-mono tabular-nums" data-testid={`hh-return-prices-${hh.id}`}>
                    Regresa a: {ORDER.filter(k => (hh.post_prices || hh.vigente_prices)[k]?.self != null).map(k => `${LABELS[k]} Q${(hh.post_prices || hh.vigente_prices)[k].self.toFixed(2)}`).join(' · ')}
                  </p>
                )}
              </>
            )}
            {startReady(hh) && !hh.start_photo_at && !isTimeUp(hh) && (
              <Button
                onClick={() => pickPhoto(hh, 'start')}
                disabled={uploading === hh.id}
                className="mt-2 w-full h-10 rounded-full text-[12.5px] text-white bg-[#15803d] hover:bg-[#166534]"
                data-testid={`hh-start-photo-btn-${hh.id}`}
              >
                {uploading === hh.id ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Camera size={13} className="mr-1.5"/>}
                Subir foto del monolito con precios HH
              </Button>
            )}
            {['active', 'awaiting_close'].includes(hh.status) && (
              <Button
                onClick={() => pickPhoto(hh, 'close')}
                disabled={uploading === hh.id}
                variant={!isTimeUp(hh) && !hh.start_photo_at ? 'outline' : 'default'}
                className={`mt-2 w-full h-10 rounded-full text-[12.5px] ${isTimeUp(hh) ? 'bg-[#991b1b] hover:bg-[#7f1616] text-white' : hh.start_photo_at ? 'bg-[#1c1c1c] hover:bg-black text-white' : 'border-[#e8e5de] text-[#5a5a5a]'}`}
                data-testid={`hh-close-btn-${hh.id}`}
              >
                {uploading === hh.id ? <Loader2 size={13} className="animate-spin mr-1.5"/> : <Camera size={13} className="mr-1.5"/>}
                {isTimeUp(hh) ? 'Subir foto de regreso y cerrar' : 'Cerrar antes de tiempo (foto de regreso)'}
              </Button>
            )}
            {hh.status === 'closed' && (
              <p className="text-[11px] text-[#3730a3] mt-1">Cerrado {fmt(hh.closed_at)} por {hh.closed_by_name}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
